import { memoColumn } from '@/shared/templates/memos'
import type { MemoBlock } from '@/shared/templates/memos'
import { ordersByTime, type MemoItem } from '@/shared/templates/memos/lib/renderMemo'
import { isTopNews } from '@/shared/templates/memos/lib/topNews'
import { holdsDocument } from './blockCopies'

/**
 * What moving an item around the preview depends on, beyond the selection
 * itself: the block a drop's neighbor lives in, and the grouping that block
 * declares. Which blocks the editor removed says nothing here - a drop is
 * aimed at something the preview is showing, so it can only land in a block
 * that is still there.
 */
export interface MoveContext {
  /** Every block the template declares, removed ones included. */
  blocks: MemoBlock[]
}

const blockFor = (item: MemoItem, blocks: MemoBlock[]) => (
  blocks.find((block) => block.id === item.blockId)
)

/**
 * Whether `item` keeps its place whatever the editor does: an event with a time
 * of day, in a block that lists its items by time. Only whole day events and
 * material can be dragged there.
 */
export function hasFixedPlace(item: MemoItem, blocks: MemoBlock[]): boolean {
  const block = blockFor(item, blocks)

  return !!item.startTimeLabel && !!block && ordersByTime(block)
}

/**
 * Whether `item` could be dropped immediately next to `candidate` without
 * rewriting a fact about `item` to make it fit. The rule is checked against
 * the block `candidate` actually lives in - that is the block whose grouping
 * would apply once the drop lands there - not the block `item` came from, so
 * this also validates drops that cross into a different block: material is
 * always rendered before events within a block (crossing that split would be
 * a no-op), and - when the block groups by day or section - only a candidate
 * sharing `item`'s own startDate/sectionTitle is reachable, since landing it
 * under a different heading would otherwise require lying about that date or
 * section.
 *
 * Top news is listed by newsvalue ahead of everything else in its block, so an
 * item only reaches another of the same standing - both top news at the same
 * newsvalue, or neither.
 *
 * A block that already holds another copy of `item`'s document is unreachable
 * too: the memo would list the same thing twice under one heading. That is only
 * ever the case on a template that puts its items in every block.
 */
export function canDropNear(
  item: MemoItem,
  candidate: MemoItem,
  blocks: MemoBlock[],
  selected: MemoItem[]
): boolean {
  if (candidate.id === item.id) {
    return false
  }

  if (holdsDocument(selected, candidate.blockId, item)) {
    return false
  }

  const block = blockFor(candidate, blocks)
  const grouping = block?.grouping ?? 'none'
  const top = isTopNews(block, item)

  if (top !== isTopNews(block, candidate) || (top && candidate.newsvalue !== item.newsvalue)) {
    return false
  }

  // Top news are listed apart from the block's days and sections.
  if (top) {
    return true
  }

  return memoColumn(candidate.sourceType) === memoColumn(item.sourceType)
    && (grouping !== 'day' || candidate.startDate === item.startDate)
    && (grouping !== 'section' || (candidate.sectionTitle ?? '') === (item.sectionTitle ?? ''))
}

/**
 * The selection with `item` moved to sit immediately before or after
 * `neighborId`. When the neighbor lives in a different block, `item` is
 * rebadged to that block - the same reassignment the preview's block-header
 * remove/reinsert controls already do - but nothing about the item's own
 * data (startDate, sectionTitle, keyword, ...) is touched.
 */
export function moveItemNear(
  selected: MemoItem[],
  item: MemoItem,
  neighborId: string,
  position: 'before' | 'after',
  { blocks }: MoveContext
): MemoItem[] {
  const neighbor = selected.find((entry) => entry.id === neighborId)

  if (!neighbor || neighbor.id === item.id) {
    return selected
  }

  const neighborBlock = blockFor(neighbor, blocks)

  if (neighborBlock && holdsDocument(selected, neighborBlock.id, item)) {
    return selected
  }

  const moved: MemoItem = neighborBlock && neighborBlock.id !== item.blockId
    ? { ...item, blockId: neighborBlock.id }
    : item

  const withoutItem = selected.filter((entry) => entry.id !== item.id)
  const neighborIndex = withoutItem.findIndex((entry) => entry.id === neighborId)

  if (neighborIndex < 0) {
    return selected
  }

  const insertAt = position === 'before' ? neighborIndex : neighborIndex + 1

  return [
    ...withoutItem.slice(0, insertAt),
    moved,
    ...withoutItem.slice(insertAt)
  ]
}

/**
 * The selection with `item` moved into `blockId`, for a drop with no
 * neighbor item to anchor to - a block that is empty, or that has nothing in
 * `item`'s column yet. Landed after the last item already in that block's
 * matching column, so it takes its place among column-mates rather than
 * displacing anything; an empty or column-mismatched block just takes it at
 * the end. `renderMemo.ts` buckets items by block/column/day/section on
 * render regardless of array position, so the exact index only matters as a
 * tie-break within that bucket.
 */
export function moveItemIntoBlock(
  selected: MemoItem[],
  item: MemoItem,
  blockId: string
): MemoItem[] {
  if (holdsDocument(selected, blockId, item)) {
    return selected
  }

  const moved: MemoItem = item.blockId === blockId ? item : { ...item, blockId }
  const withoutItem = selected.filter((entry) => entry.id !== item.id)
  const column = memoColumn(item.sourceType)

  const inTargetSameColumn = (entry: MemoItem) => (
    entry.blockId === blockId && memoColumn(entry.sourceType) === column
  )

  const lastMatch = withoutItem.map(inTargetSameColumn).lastIndexOf(true)
  const insertAt = lastMatch >= 0 ? lastMatch + 1 : withoutItem.length

  return [
    ...withoutItem.slice(0, insertAt),
    moved,
    ...withoutItem.slice(insertAt)
  ]
}
